import { IconProps } from '../Icon';
import Checkbox, { CheckboxProps } from '../Checkbox';
import { forwardRef, MouseEventHandler, ReactNode, useEffect, useState } from 'react';
import MuiTableCell, { TableCellProps as MuiTableCellProps } from '@mui/material/TableCell';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';
import { styled } from '@mui/material/styles';

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    grey: true;
  }
}

export * from '@mui/material/TableCell';

export enum CheckedState {
  checked = 'checked',
  unchecked = 'unchecked',
  indeterminate = 'indeterminate',
}

export interface TableCellProps extends MuiTableCellProps {
  /**
   * Should component block event clicks?
   * @default true
   */
  isClickable?: boolean;
  onClick?: MouseEventHandler;
  /**
   * If true, cell is rendered with right facing chevron.
   */
  hasChevron?: boolean;
  color?: 'internal';
  children?: ReactNode;
  /**
   * If true, cell is rendered with checkbox for selection.
   */
  isSelectable?: boolean;
  slotProps?: {
    checkbox?: Omit<CheckboxProps, 'checked'> & { checked?: CheckedState };
    chevron?: IconProps;
  };
}

const TableCellRoot = styled(MuiTableCell, {
  name: 'O2TableCell',
  slot: 'Root',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'ownerState',
})<any>(({ theme, ownerState }) => ({
  ...(ownerState.color === 'internal' && {
    backgroundColor: theme.palette.background.internal,
  }),
  ...(!ownerState.isClickable && {
    cursor: 'initial',
    '&:active': {
      pointerEvents: 'none',
    },
    '&:active *': {
      pointerEvents: 'auto',
    },
  }),
  ...((ownerState.isSelectable || ownerState.hasChevron) && {
    width: 40,
    minWidth: 40,
  }),
  ...(ownerState.component === 'th' && {
    fontWeight: 400,
  }),
  ...(ownerState.hasChevron && {
    width: '0%',
  }),
}));

const TableCell = forwardRef<unknown, TableCellProps>((props, ref) => {
  const {
    isClickable = true,
    onClick,
    hasChevron = false,
    color = undefined,
    isSelectable = false,
    slotProps = {},
    children,
    ...others
  } = props;
  const [checkedState, setCheckedState] = useState<CheckedState>(
    slotProps?.checkbox?.checked || CheckedState.unchecked
  );

  useEffect(() => {
    if (slotProps?.checkbox?.checked || slotProps?.checkbox?.indeterminate) {
      setCheckedState(
        slotProps.checkbox.checked ||
          (slotProps.checkbox.indeterminate ? CheckedState.indeterminate : CheckedState.unchecked)
      );
    }
  }, [slotProps?.checkbox?.checked, slotProps?.checkbox?.indeterminate]);

  const ownerState = {
    ...props,
    isClickable: isClickable && isSelectable ? false : isClickable,
    isSelectable,
    hasChevron,
    color,
  };

  const rootOnClick: MouseEventHandler = (e) => {
    if (!isClickable || isSelectable) {
      e.stopPropagation();

      if (onClick) onClick(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, i: any) => {
    setCheckedState(
      [CheckedState.unchecked, CheckedState.indeterminate].includes(checkedState)
        ? CheckedState.checked
        : CheckedState.unchecked
    );

    if (slotProps?.checkbox?.onChange) slotProps.checkbox.onChange(e, i);
  };

  return (
    <TableCellRoot ref={ref} onClick={rootOnClick} {...others} ownerState={ownerState}>
      {isSelectable && (
        <Checkbox
          {...{
            ...slotProps?.checkbox,
            checked: checkedState === CheckedState.checked,
            indeterminate: checkedState === CheckedState.indeterminate,
            onChange: handleChange,
          }}
        />
      )}
      {children}
      {hasChevron && <ChevronRightIcon {...({ color: 'grey', ...slotProps?.chevron } as any)} />}
    </TableCellRoot>
  );
});

TableCell.displayName = 'TableCell';

export default TableCell;
